import { buildServerUrl } from '../../services/api';
import type {
  ApiKeyApiScopeInput,
  ApiKeyApiScopeResponse,
  CreateApiKeyResponse,
} from '../../types';
export interface ApiKeyCurlExample {
  key: string;
  titleKey: string;
  title: string;
  command: string;
  /** Optional hint under the title listing the parameters a caller can send. */
  noteKey?: string;
  note?: string;
}
const MAX_EXAMPLES = 6;
const DEDICATED_EXAMPLE_SCOPES = new Set(['audio:agent', 'audio:transcribe', 'audio:synthesize']);
export const buildCurlCommand = ({
  apiKey,
  method,
  path,
  body,
  form,
  headers = {},
}: {
  apiKey: string;
  method: 'POST' | 'GET' | 'DELETE';
  path: string;
  body?: Record<string, unknown>;
  form?: Record<string, string>;
  headers?: Record<string, string>;
}) => {
  const lines = [`curl -X ${method} "${buildServerUrl(path)}"`, `  -H "x-api-key: ${apiKey}"`];

  Object.entries(headers).forEach(([name, value]) => {
    lines.push(`  -H "${name}: ${value}"`);
  });

  if (body) {
    lines.push('  -H "Content-Type: application/json"');
    lines.push(`  -d '${JSON.stringify(body)}'`);
  } else if (form) {
    Object.entries(form).forEach(([name, value]) => {
      lines.push(`  -F "${name}=${value}"`);
    });
  }

  return lines.join(' \\\n');
};
export const buildVoiceRealtimeClientSnippet = (): string => {
  try {
    return voiceRealtimeClientSnippet.replace(
      'API_HOST',
      new URL(buildServerUrl('/')).host || 'API_HOST'
    );
  } catch {
    return voiceRealtimeClientSnippet;
  }
};
const voiceRealtimeClientSnippet = `// Step 2 (browser): connect with the token your server received. Never ship the API key.
import { VoiceClient } from '@cloudflare/voice/client';

const { data } = await fetch('/your-backend/voice-session', { method: 'POST' }).then((r) => r.json());
const client = new VoiceClient({
  agent: data.connect.agent,
  name: data.connect.name,
  host: 'API_HOST',
  query: data.connect.query,
  preferredFormat: 'mp3',
});
client.addEventListener('transcriptchange', (messages) => render(messages));
client.addEventListener('custommessage', (msg) => {
  // { type: 'voice_error' | 'voice_artifact' | 'response_mode', ... }
});
client.connect();
micButton.onclick = () => (client.status === 'idle' ? client.startCall() : client.endCall());
// Switch answers between speech and text only:
// client.sendJSON({ type: 'set_response_mode', mode: 'text' });`;
export const getFirstScopedResourcePublicId = (
  scopes: ApiKeyApiScopeResponse[] | undefined,
  resourceType: string
) =>
  scopes?.find((scope) => scope.resourceType === resourceType && scope.resourcePublicId)
    ?.resourcePublicId;
export const enrichCreatedApiKeyScopes = (
  data: CreateApiKeyResponse,
  inputs: ApiKeyApiScopeInput[]
): CreateApiKeyResponse => ({
  ...data,
  scopes: data.scopes?.map((scope) => {
    if (scope.resourcePublicId) return scope;

    const matchingInput = inputs.find(
      (input) =>
        input.scope === scope.scope &&
        (input.resourceType ?? null) === (scope.resourceType ?? null) &&
        input.resourcePublicId
    );

    return matchingInput?.resourcePublicId
      ? { ...scope, resourcePublicId: matchingInput.resourcePublicId }
      : scope;
  }),
});
export const buildApiKeyCurlExamples = (data: CreateApiKeyResponse | null): ApiKeyCurlExample[] => {
  const scopes = data?.scopes ?? [];
  if (!data?.apiKey || scopes.length === 0) return [];
  const grantedScopes = new Set(scopes.map((scope) => scope.scope));
  const examples: ApiKeyCurlExample[] = [];
  const addExample = (example: ApiKeyCurlExample) => {
    if (!examples.some((item) => item.key === example.key)) {
      examples.push(example);
    }
  };
  const translationScope = scopes.find((scope) => scope.scope === 'translations:create');
  if (translationScope) {
    const agentPublicId =
      translationScope.resourceType === 'agent'
        ? (translationScope.resourcePublicId ?? '{agent_public_id}')
        : undefined;
    addExample({
      key: 'translation',
      titleKey: 'apiKeys.oneTime.exampleTranslation',
      title: 'Translation',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/translate',
        body: {
          text: 'Hello, welcome to our service.',
          sourceLang: 'auto',
          targetLang: ['vi'],
          ...(agentPublicId && { agentPublicId }),
        },
      }),
    });
  }
  if (grantedScopes.has('images:generate')) {
    addExample({
      key: 'image-generation',
      titleKey: 'apiKeys.oneTime.exampleImageGeneration',
      title: 'Image generation',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/capabilities/text_to_image/execute',
        body: {
          prompt: 'A bowl of pho bo with fresh herbs, top-down food photo, natural light.',
          width: 1024,
          height: 1024,
          resolution: '2K',
          ratio: '1:1',
        },
      }),
      noteKey: 'apiKeys.oneTime.noteImageGeneration',
      note: 'Optional: width, height (px), resolution (alias of imageSize, e.g. 1K/2K), ratio (alias of aspectRatio), outputFormat, seed, inputs. Food photos: 1024x1024 (1:1) for menus, 1:1 or 4:3 for dishes, 16:9 for banners.',
    });
  }
  if (grantedScopes.has('videos:generate')) {
    addExample({
      key: 'video-generation',
      titleKey: 'apiKeys.oneTime.exampleVideoGeneration',
      title: 'Video generation',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/capabilities/text_to_video/execute',
        headers: { 'Idempotency-Key': 'video_001' },
        body: {
          prompt: 'A bowl of pho bo with rising steam, smooth camera motion, food commercial.',
          durationSeconds: 5,
          ratio: '16:9',
          resolution: '1080p',
        },
      }),
      noteKey: 'apiKeys.oneTime.noteVideoGeneration',
      note: 'Optional: durationSeconds, ratio (alias of aspectRatio), resolution (model-dependent), negativePrompt, seed, inputs. Runs as a job: poll GET /v1/capabilities/jobs/{jobId}.',
    });
  }
  if (grantedScopes.has('videos:generate_from_image')) {
    addExample({
      key: 'image-to-video',
      titleKey: 'apiKeys.oneTime.exampleImageToVideo',
      title: 'Image to video',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/capabilities/image_to_video/execute',
        headers: { 'Idempotency-Key': 'video_img_001' },
        body: {
          prompt: 'Steam rises slowly from the bowl of pho, slow camera push-in.',
          images: [{ data: 'base64-image-data', mimeType: 'image/png' }],
          durationSeconds: 5,
          ratio: '1:1',
          resolution: '1080p',
        },
      }),
      noteKey: 'apiKeys.oneTime.noteImageToVideo',
      note: 'images: 1+ items, each with data (base64) + mimeType or fileAssetPublicId. Optional: durationSeconds, ratio (alias of aspectRatio), resolution (model-dependent), negativePrompt, seed, inputs. Runs as a job: poll GET /v1/capabilities/jobs/{jobId}.',
    });
  }
  if (grantedScopes.has('images:analyze')) {
    addExample({
      key: 'image-analysis',
      titleKey: 'apiKeys.oneTime.exampleImageAnalysis',
      title: 'Image analysis',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/capabilities/image_to_text/execute',
        body: {
          prompt: 'Describe this image.',
          images: [
            {
              data: 'base64-image-data',
              mimeType: 'image/png',
            },
          ],
        },
      }),
    });
  }
  if (grantedScopes.has('chat:create')) {
    addExample({
      key: 'text-generation',
      titleKey: 'apiKeys.oneTime.exampleTextGeneration',
      title: 'Text generation',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/capabilities/text_generate/execute',
        body: {
          messages: [{ role: 'user', content: 'Write a short welcome message.' }],
        },
      }),
    });
  }
  if (grantedScopes.has('agents:execute') && grantedScopes.has('chat_messages:create')) {
    const agentPublicId = getFirstScopedResourcePublicId(scopes, 'agent') ?? '{agent_public_id}';
    addExample({
      key: 'agent-message',
      titleKey: 'apiKeys.oneTime.exampleAgentMessage',
      title: 'Agent message',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: `/v1/agents/${agentPublicId}/messages`,
        headers: { 'Idempotency-Key': 'msg_001' },
        body: {
          session: {
            external_session_id: 'personal-demo',
            create_if_missing: true,
          },
          message: {
            role: 'user',
            content: 'Hello, can you help me?',
          },
        },
      }),
    });
  }
  if (grantedScopes.has('audio:agent')) {
    const agentPublicId = getFirstScopedResourcePublicId(scopes, 'agent') ?? '{agent_public_id}';
    addExample({
      key: 'voice-agent',
      titleKey: 'apiKeys.oneTime.exampleVoiceAgent',
      title: 'Voice agent turn',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/voice/agent',
        headers: { 'Idempotency-Key': 'voice_001' },
        form: {
          audio: '@question.mp3',
          agentPublicId,
          externalSessionId: 'customer-123',
          createIfMissing: 'true',
          synthesize: 'true',
          language: 'vi',
        },
      }),
    });
    addExample({
      key: 'voice-realtime-session',
      titleKey: 'apiKeys.oneTime.exampleVoiceRealtime',
      title: 'Realtime voice (step 1: server mints a session)',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/voice/realtime/sessions',
        body: {
          agentPublicId,
          externalSessionId: 'customer-123',
          createIfMissing: true,
          responseMode: 'voice',
          allowedCapabilities: ['text_generate'],
          locale: 'vi',
        },
      }),
    });
  }
  if (grantedScopes.has('audio:transcribe')) {
    addExample({
      key: 'voice-transcribe',
      titleKey: 'apiKeys.oneTime.exampleVoiceTranscribe',
      title: 'Speech to text',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/voice/transcribe',
        headers: { 'Idempotency-Key': 'stt_001' },
        form: { audio: '@audio.mp3', language: 'vi' },
      }),
    });
  }
  if (grantedScopes.has('audio:synthesize')) {
    addExample({
      key: 'voice-synthesize',
      titleKey: 'apiKeys.oneTime.exampleVoiceSynthesize',
      title: 'Text to speech',
      command: buildCurlCommand({
        apiKey: data.apiKey,
        method: 'POST',
        path: '/v1/voice/synthesize',
        headers: { 'Idempotency-Key': 'tts_001' },
        body: { text: 'Xin chào, tôi có thể giúp gì cho bạn?' },
      }),
    });
  }
  scopes
    .filter(
      (scope) =>
        scope.capabilityCode &&
        !DEDICATED_EXAMPLE_SCOPES.has(scope.scope) &&
        !['translation', 'text_to_image', 'image_to_text', 'text_generate', 'text_to_video', 'image_to_video'].includes(
          scope.capabilityCode
        )
    )
    .slice(0, Math.max(0, MAX_EXAMPLES - examples.length))
    .forEach((scope) => {
      addExample({
        key: `direct-${scope.capabilityCode}`,
        titleKey: 'apiKeys.oneTime.exampleDirectCapability',
        title: `${scope.capabilityCode} capability`,
        command: buildCurlCommand({
          apiKey: data.apiKey,
          method: 'POST',
          path: `/v1/capabilities/${scope.capabilityCode}/execute`,
          body: { prompt: 'Run this capability with my input.' },
        }),
      });
    });
  return examples.slice(0, MAX_EXAMPLES);
};
